import { type ServerResponse, axios } from '~/axios'

/**
 * Throws an error if the server signals a failure via a body-level error code
 * (HTTP 200 but code >= 400), which is a pattern used by this backend over GraphQL.
 */
function throwIfServerError<T>(response: { data: ServerResponse<T> }): void {
  const { code } = response.data
  if (typeof code === 'number' && code >= 400) {
    throw new Error(`Server error: ${code}`)
  }
}

export async function listSupportedTranslationLanguages() {
  const query = /* GraphQL */ `
    query listSupportedTranslationLanguages {
      listSupportedTranslationLanguages {
        code
        description
      }
    }
  `

  interface SupportedTranslationLanguage {
    code: string
    description: string
  }

  return await axios<
    ServerResponse<{
      listSupportedTranslationLanguages: SupportedTranslationLanguage[]
    }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query },
  })
}

export async function getTranslation(
  text: string,
  targetLanguage: string,
  sourceLanguage?: string,
) {
  const query = /* GraphQL */ `
    query getTranslation(
      $text: String
      $sourceLanguage: String
      $targetLanguage: String
    ) {
      getTranslation(
        text: $text
        sourceLanguage: $sourceLanguage
        targetLanguage: $targetLanguage
      )
    }
  `

  return await axios<ServerResponse<{ getTranslation: string }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        sourceLanguage,
        targetLanguage,
        text,
      },
    },
  })
}

export async function getAnnouncement(announcementId: number) {
  const query = /* GraphQL */ `
    query getAnnouncement($announcementId: Long) {
      getAnnouncement(announcementId: $announcementId) {
        id
        title
        content
        announcementDate
        lang
      }
    }
  `

  return await axios<ServerResponse<{ getAnnouncement: Announcement[] }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { announcementId },
    },
  })
}

export interface Announcement {
  id: number
  title: string
  content: string
  lang: string
  announcementDate: string
}

export interface PaginableAnnouncementsResponse {
  content: Announcement[]
  page: number
  totalElements: number
  totalPages: number
}

export async function listPaginableAnnouncements(
  page: number,
  pageSize: number,
  filter: string,
  from: string | null,
  to: string | null,
  selectedLanguage: string,
) {
  const query = /* GraphQL */ `
    query listPaginableAnnouncements(
      $page: Int
      $pageSize: Int
      $filter: String
      $from: String
      $to: String
    ) {
      listPaginableAnnouncements(
        page: $page
        pageSize: $pageSize
        filter: $filter
        from: $from
        to: $to
      ) {
        content {
          id
          title
          content
          lang
          announcementDate
        }
        page
        totalElements
        totalPages
      }
    }
  `
  const variables = { page, pageSize, filter, from, to }
  return await axios<
    ServerResponse<{
      listPaginableAnnouncements: PaginableAnnouncementsResponse
    }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query, variables },
  })
}

export interface AnnouncementModelInput {
  id?: number
  title: string
  content: string
  announcementDate: string
  lang: string
}

export interface AnnouncementMutationResponse {
  successful: boolean
}

export async function createAnnouncement(
  announcementModels: AnnouncementModelInput[],
) {
  const query = /* GraphQL */ `
    mutation createAnnouncement($announcementModels: [AnnouncementModelInput]) {
      createAnnouncement(announcementModels: $announcementModels) {
        successful
      }
    }
  `
  const response = await axios<
    ServerResponse<{ createAnnouncement: AnnouncementMutationResponse }>
  >({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { announcementModels },
    },
  })

  throwIfServerError(response)

  return response
}

export async function updateAnnouncement(
  announcementModels: AnnouncementModelInput[],
) {
  const query = /* GraphQL */ `
    mutation updateAnnouncement($announcementModels: [AnnouncementModelInput]) {
      updateAnnouncement(announcementModels: $announcementModels) {
        successful
      }
    }
  `
  const response = await axios<
    ServerResponse<{ updateAnnouncement: AnnouncementMutationResponse }>
  >({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { announcementModels },
    },
  })

  throwIfServerError(response)

  return response
}

export async function deleteAnnouncement(announcementId: number) {
  const query = /* GraphQL */ `
    mutation deleteAnnouncement($announcementId: Long) {
      deleteAnnouncement(announcementId: $announcementId) {
        successful
      }
    }
  `
  const response = await axios<
    ServerResponse<{ deleteAnnouncement: AnnouncementMutationResponse }>
  >({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: { announcementId },
    },
  })

  throwIfServerError(response)

  return response
}
